import { useState } from "react";
import { CorporationsEntity } from "../../../../entities/Corporations/CorporationsEntity";
import { useTranslation } from "react-i18next";
import Button from "@mui/material/Button";
import { MainTable } from "../../../../components";
import { IMainTableColumn } from "../../../../components/MainTable/MainTableInterfaces";
import { CorporationForm } from "./CorporationForm/CorporationForm.view";
import { useStyles } from "./Corporations.styles";
import AddIcon from "@mui/icons-material/Add";
import { MADERA_BACKOFFICE_PATHS, USERTYPE } from "../../../../constants/constants";
import { MillForm } from "./MillForm/MillForm.view";
import { Step, StepLabel, Stepper } from "@mui/material";
import { ICorporateReponse } from "./CorporationForm/CorporationFormInterfaces";
import { ICorporation, StepsEnum } from "./CorporationsAPIInterfaces";
import { useNavigate } from "react-router-dom";
import useCorporationsColumns from "./hooks/useCorporationsColumns/useCorporationsColumns";
import { FeatureToggleConfirmModal } from "pages/Osorno/components";
import useCorporationsLicenses, { ModuleLicenseStatusEnum } from "./hooks/useCorporationsLicenses";
import { useFeatureToggleModal } from "pages/Osorno/hooks/useFeatureToggleModal";

export const Corporations = () => {
    const classes = useStyles();
    const { t } = useTranslation();
    const [creating, setCreating] = useState<boolean>(false);
    const navigate = useNavigate();
    const { loading, corporations, apolloQueryError, refetch } = CorporationsEntity.GetCorporations(
        USERTYPE.ADMINISTRATOR,
    );
    const { updateCorporateRcaEnabled } = CorporationsEntity.UpdateCorporateRcaEnabledOsornoBO();
    const [activeStep, setActiveStep] = useState(StepsEnum.corporationSetup);
    const [newCorporation, setNewCorporation] = useState<ICorporateReponse | undefined>(undefined);
    const [corporationEditData, setCorporationEditData] = useState<ICorporation | undefined>(undefined);
    const birchModal = useFeatureToggleModal<ICorporation>("setup_corporation.birch_modal");
    const moduleLicenseModal = useFeatureToggleModal<ICorporation>(
        "setup_corporation.module_license_modal",
    );
    const { getModuleLicenseStatus, activateModuleLicense, reactivateModuleLicense, deactivateModuleLicense } =
        useCorporationsLicenses();

    const tableColumns = useCorporationsColumns(
        setCorporationEditData,
        birchModal.openModal,
        moduleLicenseModal.openModal,
    ) as unknown as IMainTableColumn[];

    const handleOpenForm = () => {
        setCreating(true);
        setActiveStep(StepsEnum.corporationSetup);
    };

    const handleCloseForm = async () => {
        setCreating(false);
        setCorporationEditData(undefined);
        await refetch();
    };

    const handleNextStep = (corporation: ICorporateReponse) => {
        setNewCorporation(corporation);
        setActiveStep(StepsEnum.millSetup);
    };

    const steps = [
        {
            id: StepsEnum.corporationSetup,
            label: t("setup_corporation.label_steps.add_corporation"),
        },
        {
            id: StepsEnum.millSetup,
            label: t("setup_corporation.label_steps.add_mill"),
        },
    ];

    const stepsComponents = {
        [StepsEnum.corporationSetup]: {
            component: <CorporationForm closeForm={handleCloseForm} nextStep={handleNextStep} />,
        },
        [StepsEnum.millSetup]: {
            component: <MillForm corporation={newCorporation && newCorporation} closeForm={handleCloseForm} />,
        },
    };

    const handleBirchModalConfirm = async () => {
        if (birchModal.row) {
            await updateCorporateRcaEnabled({
                variables: {
                    corporate_id: birchModal.row.corporate_id,
                    rca_enabled: birchModal.enable,
                },
            });
            await refetch();
        }
    };

    const handleModuleLicenseModalConfirm = async () => {
        if (moduleLicenseModal.row) {
            if (moduleLicenseModal.enable) {
                const status = getModuleLicenseStatus(moduleLicenseModal.row);
                if (status === ModuleLicenseStatusEnum.No_Activated) {
                    await activateModuleLicense(moduleLicenseModal.row);
                } else {
                    await reactivateModuleLicense(moduleLicenseModal.row);
                }
            } else {
                await deactivateModuleLicense(moduleLicenseModal.row);
            }
            await refetch();
        }
    };

    return !creating && !corporationEditData ? (
        <>
            <FeatureToggleConfirmModal
                {...birchModal.modalProps}
                onConfirm={handleBirchModalConfirm}
            />
            <FeatureToggleConfirmModal
                {...moduleLicenseModal.modalProps}
                onConfirm={handleModuleLicenseModalConfirm}
            />
            <div className={classes.container}>
                <h2 className={classes.title}>{t("setup_corporation.table_title")}</h2>
                <Button variant="contained" className={classes.addButton} onClick={handleOpenForm}>
                    <AddIcon fontSize="small" style={{ marginRight: "5px" }} />
                    {t("setup_corporation.add_corporation_button")}
                </Button>
            </div>
            <MainTable
                columns={tableColumns}
                rows={!loading && !apolloQueryError ? corporations : []}
                loading={loading}
                onRowClick={(row: { corporate_id?: number }) => {
                    navigate(
                        `/${MADERA_BACKOFFICE_PATHS.ROOT_PATH}/${MADERA_BACKOFFICE_PATHS.SETUP_DASHBOARD.CORPORATION.ROOT}/${row.corporate_id}`,
                    );
                }}
                tableCellCustomStyle={classes.tableRow}
                pagination
            />
        </>
    ) : creating ? (
        <div className={classes.contentWrapper}>
            <Stepper activeStep={activeStep}>
                {steps.map((step) => (
                    <Step key={step.id} className={classes.step}>
                        <StepLabel>{step.label}</StepLabel>
                    </Step>
                ))}
            </Stepper>
            {stepsComponents[activeStep].component}
        </div>
    ) : (
        <CorporationForm
            corporationEditData={corporationEditData}
            closeForm={handleCloseForm}
            nextStep={handleCloseForm}
        />
    );
};
